/* «Карточка для маркетплейса» (APP-SPEC-5 §11): фото товара один раз → «Что сделать N из 4» с вариантами у каждой работы →
   «Название и преимущества» с «Заполнить из описания» → «Ещё настройки» (Сколько фото · Площадка · Пожелания) → «Создать всё».
   Один заказ на все отмеченные работы; хвост — вкладки результата по секциям сервера (ProductOrderResult).
   Ключи и значения — только опубликованные формой (slides/product-model.ts KEYS); варианты, площадка и пожелания
   появляются, когда форма публикует их поля. Цена — expected_price формы: своей арифметики цен на клиенте нет. */
import { useMemo, useState } from 'react';
import type { UserAvatar } from '../../api/people';
import { uiFormat, uiText } from '../../i18n';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import { TextEditor } from '../../ui/TextEditor';
import { TextField } from '../../ui/TextField';
import { StudioCreateButton, StudioSettings, StudioSettingsSection, type StudioSettingSlot } from '../studio/StudioFormParts';
import { ToolHeader } from '../studio/circle/ToolHeader';
import { useTailRetry } from '../studio/circle/tool-helpers';
import { StudioPhotoTray } from '../studio/StudioPhotoTray';
import { studioCheapOffer, studioExpectedPrice, type StudioApp } from '../studio/studio-api';
import { useStudioForm } from '../studio/use-studio-form';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import {
  count,
  DESC_TONES,
  enumOf,
  INFOG_STYLES,
  joinProductText,
  label,
  MCARD_MOTIONS,
  MCARD_SETS,
  MCARD_WISHES,
  PHOTO_FORMS,
  photoSlots,
  platformCell,
  PLATFORMS,
  splitProductText,
  textOf,
  utf16,
  UUID,
  type Choice,
  type ControlKey,
} from '../slides/product-model';
import { ProductExtractSheet } from '../slides/ProductExtractSheet';
import { ProductOrderResult } from '../slides/ProductOrderResult';
import { ProductVariantRail } from '../slides/ProductVariantRail';
import { useProductDefaults } from '../slides/use-product-defaults';
import { MCARD_DEFAULTS, MCARD_INITIAL, mcardContractSupported } from './mcard-model';
import './mcard.css';
import './native-mcard.css';
import '../slides/product.css';

interface Operation {
  id: 'photos' | 'slides' | 'video' | 'description';
  name: string;
  variant: ControlKey;
  dict: Record<string, Choice>;
  rail: string;
  all: string;
  meta?: string;
}
const operations: Operation[] = [
  { id: 'photos', name: 'Фото в сценах', variant: 'photoSet', dict: MCARD_SETS, rail: 'Набор сцен', all: 'Все наборы' },
  { id: 'slides', name: 'Слайды с преимуществами', variant: 'slidesStyle', dict: INFOG_STYLES, rail: 'Оформление', all: 'Все оформления', meta: '3 слайда' },
  { id: 'video', name: 'Видео товара', variant: 'videoMotion', dict: MCARD_MOTIONS, rail: 'Движение', all: 'Все движения', meta: '5 сек' },
  { id: 'description', name: 'Описание', variant: 'descTone', dict: DESC_TONES, rail: 'Стиль текста', all: 'Все стили текста' },
];
export function MCardOrderForm({
  headerActive = true,
  ...props
}: {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  headerActive?: boolean;
}) {
  const initialInputs = useMemo(
    () => ({ ...MCARD_INITIAL, ...props.initialInputs }),
    [props.initialInputs],
  );
  const state = useStudioForm({ ...props, initialInputs });
  const { form, inputs, locked } = state;
  const supported = !!form && mcardContractSupported(form);
  const slots = supported ? photoSlots(form) : [];
  const previews = useStudioPhotoPreviews(state, slots);
  const fields = {
    outputs: enumOf(form, 'outputs'),
    count: enumOf(form, 'photoCount'),
    text: textOf(form, 'text'),
    name: textOf(form, 'text') ? undefined : textOf(form, 'name'),
    advantages: textOf(form, 'text') ? undefined : textOf(form, 'advantages'),
    platform: enumOf(form, 'platform'),
    wishes: textOf(form, 'wishes'),
  };
  const variantField = (operation: Operation) => enumOf(form, operation.variant);
  useProductDefaults(state, MCARD_DEFAULTS);
  const [editor, setEditor] = useState<'text' | 'wishes' | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const selected = (inputs[fields.outputs?.key ?? ''] ?? '').split(',').filter(Boolean);
  const set = (key: string | undefined, value: string) => {
    if (key) state.setInputs(previous => ({ ...previous, [key]: value }));
  };
  const toggle = (id: string) => {
    const next = operations
      .filter(item => selected.includes(item.id) !== (item.id === id))
      .map(item => item.id)
      .join(',');
    // Пустой выбор возможен при правке, но заказа без работ нет: в форму уходит только опубликованное сочетание.
    if (!next || fields.outputs?.values.includes(next)) set(fields.outputs?.key, next);
  };
  const split = !!fields.name;
  const mainText = split
    ? (draft ?? joinProductText(inputs[fields.name!.key] ?? '', (fields.advantages ? (inputs[fields.advantages.key] ?? '') : '').split('\n')))
    : (inputs[fields.text?.key ?? ''] ?? '');
  const limit = split ? 600 : (fields.text?.max_length ?? 2000);
  const setMain = (value: string) => {
    if (!split) return set(fields.text?.key, value);
    setDraft(value);
    const parts = splitProductText(value);
    state.setInputs(previous => ({
      ...previous,
      [fields.name!.key]: parts.name,
      ...(fields.advantages ? { [fields.advantages.key]: parts.advantages.join('\n') } : {}),
    }));
  };
  const product = splitProductText(mainText);
  const mainSlot = slots[0];
  const mainPhoto = mainSlot ? previews.urls[mainSlot.key] : undefined;
  const platform = fields.platform ? inputs[fields.platform.key] : undefined;
  const photoCount = fields.count ? inputs[fields.count.key] : undefined;
  const textRequired = fields.text?.required || fields.name?.required;
  const missing = !form
    ? 'Загружаем…'
    : !supported
      ? 'Этот вариант карточки пока недоступен'
      : !state.rights
        ? 'Подтвердите права на фото'
        : slots.some(field => field.required && !inputs[field.key])
          ? 'Загрузите фото товара'
          : slots.some(field => inputs[field.key] && !UUID.test(inputs[field.key]))
            ? 'Проверьте загрузку фото'
            : !fields.outputs?.values.includes(inputs[fields.outputs.key] ?? '')
              ? 'Выберите, что сделать'
              : textRequired && !mainText.trim()
                ? 'Добавьте название и преимущества'
                : utf16(mainText) > limit
                  ? 'Сократите текст'
                  : selected.includes('slides') && (Array.from(product.name).length < 2 || product.advantages.length < 3)
                    ? 'Слайдам нужны название и три преимущества'
                    : selected.includes('description') && !product.name
                      ? 'Описанию нужно название товара'
                      : selected.includes('photos') && fields.count && !fields.count.values.includes(photoCount ?? '')
                        ? 'Выберите количество фото'
                        : fields.wishes && utf16(inputs[fields.wishes.key]) > (fields.wishes.max_length ?? 300)
                          ? 'Сократите пожелания'
                          : null;
  const create = () => {
    if (!state.busy && !state.submissionBlocked && (state.intent || (supported && !missing && form?.available))) void state.submit();
  };
  const available = operations.filter(item => fields.outputs?.values.some(value => value.split(',').includes(item.id)));
  const meta = (operation: Operation) => {
    const field = variantField(operation);
    const variant = field ? label(operation.dict, inputs[field.key]) : '';
    const amount = operation.id === 'photos' ? (photoCount ? count(photoCount, PHOTO_FORMS) : '') : (operation.meta ?? '');
    return [variant, amount].filter(Boolean).join(' · ');
  };
  const wishes = fields.wishes ? (inputs[fields.wishes.key] ?? '') : '';
  /* «Ещё настройки» (§11 п. 5): «Сколько фото» — только при отмеченном «Фото в сценах», «Площадка», «Пожелания». */
  const cells: StudioSettingSlot[] = [
    ...(selected.includes('photos') && fields.count && photoCount
      ? [{ title: 'Сколько фото', value: count(photoCount, PHOTO_FORMS), section: 'count' }]
      : []),
    ...(fields.platform ? [{ title: 'Площадка', value: platformCell(platform), section: 'platform' }] : []),
    ...(fields.wishes ? [{ title: 'Пожелания', value: wishes, empty: !wishes.trim(), kind: 'text' as const, section: 'wishes' }] : []),
  ];
  const retry = useTailRetry(state);
  const variantPreview = (operation: Operation) => (value: string) => (
    <span className={`px-mv px-mv-${operation.id}`}>
      {mainPhoto ? <img src={mainPhoto} alt="" /> : <span className="px-ig-ph" aria-hidden="true" />}
      {operation.id === 'description' && <b>{product.name || uiText(operation.dict[value]?.name ?? value)}</b>}
    </span>
  );
  return (
    <section className="native-studio-form native-mcard ap-in product-form">
      <ToolHeader title={props.app.name} enabled={headerActive} />
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {state.error && (
        <p role="alert">
          {uiText(state.error)}{' '}
          <button type="button" disabled={state.busy} onClick={() => state.setRevision(value => value + 1)}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {form && !state.orderId && (
        <form
          className="pf-body mc-body"
          onSubmit={event => {
            event.preventDefault();
            create();
          }}
        >
          <StudioPhotoTray
            state={state}
            slots={slots}
            urls={previews.urls}
            minimum={Math.max(1, slots.filter(slot => slot.required).length)}
            title="Фото товара"
            description={
              slots.length > 1
                ? uiFormat('До {0} ракурсов на любом фоне: загрузите один раз для всего, что ниже', slots.length)
                : 'Фото товара используется для всех выбранных работ'
            }
            consent={
              <label className="photo-consent">
                <input type="checkbox" checked={state.rights} disabled={locked} onChange={event => state.setRights(event.target.checked)} />
                {uiText('Подтверждаю право использовать фото товара')}
              </label>
            }
          />
          {previews.error && (
            <p role="alert">
              {uiText('Не удалось загрузить превью.')}{' '}
              <button type="button" onClick={previews.retry}>
                {uiText('Повторить')}
              </button>
            </p>
          )}
          <div className="mc-ops" role="group" aria-label={uiText('Что сделать')}>
            <h3 className="gl mc-gl">
              {uiText('Что сделать')}{' '}
              <span className="mono">{uiFormat('{0} из {1}', selected.filter(id => available.some(item => item.id === id)).length, available.length)}</span>
            </h3>
            <div className="mc-opl">
              {available.map(item => {
                const on = selected.includes(item.id);
                const field = variantField(item);
                return (
                  <div key={item.id} className={`mc-op${on ? ' on' : ''}`}>
                    <button
                      type="button"
                      className="mc-oh"
                      disabled={locked}
                      aria-pressed={on}
                      aria-label={uiText(item.name)}
                      onClick={() => toggle(item.id)}
                    >
                      <span className="m">
                        <b>{uiText(item.name)}</b>
                        {meta(item) && <span className="mc-mt">{uiText(meta(item))}</span>}
                      </span>
                      <span className="ck" aria-hidden="true">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="m3 8 3 3 7-7" />
                        </svg>
                      </span>
                    </button>
                    {on && (field || (item.id === 'photos' && fields.count)) && (
                      <div className="mc-ob">
                        {field && (
                          <ProductVariantRail
                            title={item.rail}
                            allTitle={item.all}
                            size="sm"
                            variants={field.values.map(value => ({ value, choice: item.dict[value] ?? { name: value } }))}
                            value={inputs[field.key] ?? ''}
                            disabled={locked}
                            onChange={value => set(field.key, value)}
                            preview={variant => variantPreview(item)(variant.value)}
                          />
                        )}
                        {item.id === 'photos' && fields.count && (
                          <ChoiceTiles
                            label={uiText('Сколько фото')}
                            value={photoCount ?? ''}
                            disabled={locked}
                            onChange={value => set(fields.count?.key, value)}
                            choices={fields.count.values.map(value => ({ value, label: value }))}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {(fields.text || fields.name) && (
            <TextField
              label={uiText('Название и преимущества')}
              value={mainText}
              limit={limit}
              disabled={locked}
              placeholder={uiText('Первая строка — название, дальше по преимуществу на строку')}
              onChange={setMain}
              onExpand={() => setEditor('text')}
              expandLabel={uiText('Во весь экран')}
            />
          )}
          {supported && (
            <ProductExtractSheet
              kind="mcard"
              disabled={locked}
              onFill={found => setMain(joinProductText(found.name || product.name, found.advantages.length ? found.advantages : product.advantages))}
            />
          )}
          {supported && cells.length > 0 && (
            <StudioSettings disabled={locked} slots={cells} oneRow>
              {selected.includes('photos') && fields.count && (
                <StudioSettingsSection id="count" title="Сколько фото">
                  <ChoiceTiles
                    label={uiText('Сколько фото')}
                    variant="rows"
                    value={photoCount ?? ''}
                    disabled={locked}
                    onChange={value => set(fields.count?.key, value)}
                    choices={fields.count.values.map(value => ({
                      value,
                      label: count(value, PHOTO_FORMS),
                      description: typeof fields.count?.prices?.[value] === 'number' ? `${fields.count.prices[value]} C` : undefined,
                    }))}
                  />
                </StudioSettingsSection>
              )}
              {fields.platform && (
                <StudioSettingsSection id="platform" title="Площадка">
                  <ChoiceTiles
                    label={uiText('Площадка')}
                    variant="rows"
                    value={platform ?? ''}
                    disabled={locked}
                    onChange={value => set(fields.platform?.key, value)}
                    choices={fields.platform.values.map(value => ({
                      value,
                      label: uiText(label(PLATFORMS, value)),
                      description: PLATFORMS[value]?.sub ? uiText(PLATFORMS[value].sub!) : undefined,
                    }))}
                  />
                </StudioSettingsSection>
              )}
              {fields.wishes && (
                <StudioSettingsSection id="wishes" title="Пожелания" kind="text" hint="необязательно">
                  <TextField
                    label={uiText('Пожелания')}
                    value={wishes}
                    limit={fields.wishes.max_length ?? 300}
                    disabled={locked}
                    placeholder={uiText('К фото и видео: свет, фон, настроение')}
                    onChange={value => set(fields.wishes?.key, value)}
                    onExpand={() => setEditor('wishes')}
                    expandLabel={uiText('Во весь экран')}
                  >
                    <div className="chips">
                      {MCARD_WISHES.map(chip => (
                        <button
                          key={chip}
                          type="button"
                          className={`chip${wishes.includes(chip.toLowerCase()) ? ' on' : ''}`}
                          disabled={locked}
                          onClick={() => set(fields.wishes?.key, [wishes.trim(), chip.toLowerCase()].filter(Boolean).join(', '))}
                        >
                          {uiText(chip)}
                        </button>
                      ))}
                    </div>
                  </TextField>
                </StudioSettingsSection>
              )}
            </StudioSettings>
          )}
          <StudioCreateButton
            kind="photo"
            label={selected.length > 1 ? 'Создать всё' : 'Создать'}
            price={studioExpectedPrice(form, inputs)}
            busy={state.busy}
            blocked={state.submissionBlocked}
            pending={!!state.intent}
            missing={missing ? uiText(missing) : null}
            available={form.available}
            balance={state.balance}
            cheap={selected.some(id => id !== 'description') ? studioCheapOffer(form) : null}
            onCheap={() => void state.submit({ cheap: true })}
            onCreate={create}
          />
        </form>
      )}
      {editor === 'text' && (
        <TextEditor
          label={uiText('Название и преимущества')}
          value={mainText}
          limit={limit}
          disabled={locked}
          onChange={setMain}
          onClose={() => setEditor(null)}
        />
      )}
      {editor === 'wishes' && fields.wishes && (
        <TextEditor
          label={uiText('Пожелания · Ещё настройки')}
          value={wishes}
          limit={fields.wishes.max_length ?? 300}
          disabled={locked}
          onChange={value => set(fields.wishes?.key, value)}
          onClose={() => setEditor(null)}
        />
      )}
      <ProductOrderResult
        state={state}
        kind="mcard"
        hero={mainPhoto}
        onRetry={retry}
        keys={{
          outputs: fields.outputs?.key,
          photoCount: fields.count?.key,
          platform: fields.platform?.key,
          photoSet: enumOf(form, 'photoSet')?.key,
          slidesStyle: enumOf(form, 'slidesStyle')?.key,
          videoMotion: enumOf(form, 'videoMotion')?.key,
          descTone: enumOf(form, 'descTone')?.key,
          text: fields.text?.key ?? fields.name?.key,
          photos: slots.map(slot => slot.key),
        }}
      />
    </section>
  );
}
